import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Plus, Star, X } from 'lucide-react';
import { useCompanies, useCreateCompany } from '../../hooks/useCompanies';
import {
  CompanyFormFields,
  companyFormToInput,
  EMPTY_COMPANY_FORM,
  type CompanyFormState,
} from './CompanyProfileSettings';
import { useI18n } from '../../contexts/I18nContext';

// Settings → "Firme" (multi-company). Lists all firms (default first); each
// row links to the firm's own page (/settings/companies/:id) where editing,
// set-default, delete and the per-firm SmartBill configuration live. This tab
// only keeps the "add firm" modal — after creating, we jump straight to the
// new firm's page. Backend: /api/companies, gated settings.canManageCompanies.

export function CompaniesSettings() {
  const { t } = useI18n();
  const { data: companies, isLoading, isError } = useCompanies();

  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">{t('companies.description')}</p>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="ui-btn-primary shrink-0"
        >
          <Plus size={15} className="mr-1 inline-block" />
          {t('companies.add')}
        </button>
      </div>

      {isLoading && <p className="mt-6 text-sm text-gray-500">{t('companies.loading')}</p>}
      {isError && <p className="mt-6 text-sm text-red-600">{t('companies.error.load')}</p>}

      {companies && (
        <ul className="mt-6 flex flex-col gap-2">
          {companies.map((company) => (
            <li key={company.id}>
              <Link
                to={`/settings/companies/${company.id}`}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:bg-gray-50"
              >
                <Building2 size={18} className="shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {company.displayName || company.legalName}
                    </span>
                    {company.isDefault && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                        <Star size={11} />
                        {t('companies.defaultBadge')}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-gray-500">
                    {company.displayName ? `${company.legalName} · ` : ''}
                    {company.cui || t('companies.noCui')}
                  </p>
                </div>
                <ChevronRight size={16} className="shrink-0 text-gray-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {showCreate && <CreateCompanyModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

// --- Add modal ------------------------------------------------------------
// Editing happens on the firm detail page; this modal only creates the firm
// and then navigates there (SmartBill config needs an existing company id).

function CreateCompanyModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const create = useCreateCompany();

  const [form, setForm] = useState<CompanyFormState>(EMPTY_COMPANY_FORM);
  const [error, setError] = useState<string | null>(null);

  function patch(next: Partial<CompanyFormState>) {
    setForm((prev) => ({ ...prev, ...next }));
  }

  async function handleSave() {
    if (!form.legalName.trim()) {
      setError(t('companies.legalNameRequired'));
      return;
    }
    setError(null);
    try {
      const created = await create.mutateAsync(companyFormToInput(form));
      onClose();
      navigate(`/settings/companies/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('companies.error.generic'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{t('companies.addTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-700"
            aria-label={t('common.cancel')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-4">
          <CompanyFormFields form={form} update={patch} />
        </div>

        {error && (
          <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="ui-btn-secondary">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={create.isPending}
            className="ui-btn-primary disabled:opacity-60"
          >
            {create.isPending ? t('companies.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
